// «Mening kirishlarim» — `GET /api/auth/my-logins`.
//
// ⚠️ KONTRAKT BACKENDDAN O'QIB OLINGAN, taxmin emas:
// `xsib-identity` → `auth/resource/AuthResource.java#myLogins`,
// `auth/dto/MyLoginView.java`, `auth/dto/MyLoginPage.java`.
//
// ⚠️ `userId` PARAMETRI YO'Q va QO'SHILMAYDI. Server identifikatorni
// TOKENDAN oladi (`AccessGuard.requireUser()`) va parametrni umuman
// qabul qilmaydi — `MyLoginsUserIdGuardTest` uni majburlaydi. Panel
// tomonda uni yozib qo'yish sizishga olib kelmasdi (server e'tiborsiz
// qoldirardi), lekin «boshqa odamning kirishlarini ko'rsatish mumkin»
// degan yolg'on taassurot qoldirardi — shuning uchun bu yerda ham
// qorovul turibdi (`tests/guards.mjs` §13).
//
// ⚠️ Yo'l `/api/auth/*` da, `/api/partner/*` da EMAS: bu kirish oqimi
// emas, o'z ma'lumoti. `client.ts` dagi `AUTH_FLOW_PATHS` ga tushmaydi,
// ya'ni 401 da token odatdagidek yangilanadi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema } from '@/shared/api/schemas'

export const MY_LOGINS_PATH = '/api/auth/my-logins'

/**
 * Kirish izini tashkil qiluvchi amallar — `MyLoginsService.LOGIN_ACTIONS`
 * ning nusxasi.
 *
 * ⚠️ Bu ro'yxat FILTR EMAS — server baribir faqat shu uchtasini
 * qaytaradi. U faqat «tarjimasi bormi» degan savolga javob beradi:
 * notanish kod ekranda XOM holda ko'rsatiladi ({@link isKnownAction}),
 * chunki noto'g'ri tarjima qilingan amal kodi noto'g'ri xavfsizlik
 * xulosasiga olib kelardi.
 */
export const LOGIN_ACTIONS = ['TOTP_VERIFY', 'TOTP_SETUP_CONFIRM', 'TENANT_SWITCH'] as const
export type LoginAction = (typeof LOGIN_ACTIONS)[number]

/** `OK` — kirdi, `FAILED` — rad etildi (`MyLoginView.result`) */
export const LOGIN_RESULTS = ['OK', 'FAILED'] as const
export type LoginResult = (typeof LOGIN_RESULTS)[number]

/**
 * Sukut oynasi — `MyLoginsService.DEFAULT_WINDOW`.
 *
 * ⚠️ EKRANDA AYTILADI: sanasiz so'rov «boshidan» degani EMAS, server
 * jimgina 90 kun bilan cheklaydi. Buni aytmaslik «eski kirishim yo'q
 * ekan» degan noto'g'ri xulosaga olib kelardi.
 */
export const MY_LOGINS_WINDOW_DAYS = 90

/** Bir sahifadagi qatorlar — serverning sukut qiymati bilan bir xil */
export const MY_LOGINS_PAGE_SIZE = 50

const myLoginRowSchema = z.object({
  at: instantSchema,
  /**
   * ⚠️ `z.enum(...)` EMAS. Server yangi natija kodini qo'shsa qat'iy
   * sxema BUTUN ro'yxatni yiqitardi va odam kirish tarixini umuman
   * ko'rmay qolardi — xavfsizlik ekranida bu eng yomon natija.
   */
  result: z.string(),
  action: z.string(),
  /**
   * ⚠️ `.nullable().optional()` — IKKALA shakl ham uchraydi va bu
   * o'lchangan: `non_null` sozlamasi tufayli kalit javobda UMUMAN
   * bo'lmasligi mumkin, sinov muhitida esa `null` bo'lib keladi.
   * Faqat bittasini yozish ikkinchisida `parse` ni yiqitardi.
   *
   * ⚠️ ESKI YOZUVDA `null` va u HECH QACHON to'lmaydi: `audit_log`
   * append-only (`audit_log_no_update`). Ekranda «yo'q» emas,
   * «noma'lum» deb ko'rsatiladi — bo'sh katak «IP bo'lmagan» degan
   * yolg'onni berardi.
   */
  ip: z.string().nullable().optional(),
  /**
   * ⚠️ `ip` bilan AYNI sabab. Ustiga: bu qiymat MIJOZ yuboradigan matn
   * (`curl -A "..."`) va uni o'zgartirish mumkin — ekranda u «Qurilma»
   * emas, «Qurilma (brauzer aytgan)» deb yoziladi.
   */
  userAgent: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
})

const myLoginPageSchema = z.object({
  rows: z.array(myLoginRowSchema),
  page: z.number(),
  size: z.number(),
  /**
   * ⚠️ `total` YO'Q va qo'shilmaydi (`MyLoginPage` javadoc'i): manba
   * cheksiz o'sadigan append-only jurnal va har sahifada `COUNT(*)`
   * butun jadvalni o'qirdi. Panel ham jami sonni HISOBLAB CHIQARMAYDI.
   */
  hasNext: z.boolean(),
})

export interface MyLogin {
  /** UTC ISO; ekranda tenant zonasida */
  at: string
  result: string
  action: string
  /** `null` — eski yozuvda saqlanmagan, «IP bo'lmagan» degani EMAS */
  ip: string | null
  /** `null` — eski yozuvda saqlanmagan. Dalil emas, brauzerning gapi */
  userAgent: string | null
  message: string | null
}

export interface MyLoginPage {
  rows: MyLogin[]
  page: number
  size: number
  hasNext: boolean
}

/** Amal kodining tarjimasi bormi. Yo'q bo'lsa kodning O'ZI ko'rsatiladi */
export function isKnownAction(action: string): action is LoginAction {
  return (LOGIN_ACTIONS as readonly string[]).includes(action)
}

/** Natija kodining tarjimasi va rangi bormi */
export function isKnownResult(result: string): result is LoginResult {
  return (LOGIN_RESULTS as readonly string[]).includes(result)
}

/**
 * O'z kirish jurnali.
 *
 * @param page 0 dan boshlanadi; sahifalash SERVERDA
 * @param size bir sahifadagi qatorlar
 */
export async function fetchMyLogins(
  page = 0,
  size = MY_LOGINS_PAGE_SIZE,
): Promise<MyLoginPage> {
  const response = await api.get<unknown>(MY_LOGINS_PATH, { params: { page, size } })
  const raw = myLoginPageSchema.parse(response.data)
  return {
    rows: raw.rows.map((row) => ({
      at: row.at,
      result: row.result,
      action: row.action,
      ip: row.ip ?? null,
      userAgent: row.userAgent ?? null,
      message: row.message ?? null,
    })),
    page: raw.page,
    size: raw.size,
    hasNext: raw.hasNext,
  }
}
